import React, { useState, useEffect, useReducer, useContext, useCallback, useRef } from "react";
import { makeStyles } from "@material-ui/core/styles";
import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  IconButton,
  TextField,
  InputAdornment,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Chip,
  Button,
  Box,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  Divider,
  Tooltip,
} from "@material-ui/core";
import {
  Search as SearchIcon,
  Visibility as VisibilityIcon,
  Refresh as RefreshIcon,
} from "@material-ui/icons";
import { toast } from "react-toastify";

import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import MainHeaderButtonsWrapper from "../../components/MainHeaderButtonsWrapper";
import Title from "../../components/Title";
import TableRowSkeleton from "../../components/TableRowSkeleton";
import * as pharmaApi from "../../services/pharmaApi";
import toastError from "../../errors/toastError";
import { AuthContext } from "../../context/Auth/AuthContext";

// Auto-refresh interval options
const REFRESH_OPTIONS = [
  { value: 0, label: "Desativado" },
  { value: 15000, label: "15 segundos" },
  { value: 30000, label: "30 segundos" },
  { value: 60000, label: "1 minuto" },
  { value: 120000, label: "2 minutos" },
  { value: 300000, label: "5 minutos" },
];

const reducer = (state, action) => {
  switch (action.type) {
    case "LOAD_ORDERS":
      return [...action.payload];
    case "UPDATE_ORDER":
      const order = action.payload;
      const orderIndex = state.findIndex((o) => o.id === order.id);
      if (orderIndex !== -1) {
        state[orderIndex] = order;
        return [...state];
      }
      return [order, ...state];
    case "RESET":
      return [];
    default:
      return state;
  }
};

const useStyles = makeStyles((theme) => ({
  mainPaper: {
    flex: 1,
    padding: theme.spacing(1),
    overflowY: "auto",
    ...theme.scrollbarStyles,
  },
  filterContainer: {
    display: "flex",
    gap: theme.spacing(2),
    marginBottom: theme.spacing(2),
    flexWrap: "wrap",
  },
  statusPending: { backgroundColor: "#ff9800", color: "#fff" },
  statusReceived: { backgroundColor: "#8bc34a", color: "#fff" },
  statusConfirmed: { backgroundColor: "#2196f3", color: "#fff" },
  statusPreparing: { backgroundColor: "#9c27b0", color: "#fff" },
  statusReady: { backgroundColor: "#4caf50", color: "#fff" },
  statusDispatched: { backgroundColor: "#00bcd4", color: "#fff" },
  statusDelivered: { backgroundColor: "#388e3c", color: "#fff" },
  statusCancelled: { backgroundColor: "#f44336", color: "#fff" },
  orderDetails: {
    minWidth: 500,
  },
  itemsTable: {
    marginTop: theme.spacing(2),
  },
  refreshing: {
    animation: "$spin 1s linear infinite",
  },
  "@keyframes spin": {
    from: { transform: "rotate(0deg)" },
    to: { transform: "rotate(360deg)" },
  },
  headerActions: {
    display: "flex",
    alignItems: "center",
    gap: 8,
  },
}));

const statusLabels = {
  pending: "Pendente",
  received: "Recebido",
  confirmed: "Confirmado",
  preparing: "Em Separação",
  ready: "Pronto",
  dispatched: "Saiu para Entrega",
  delivered: "Entregue",
  cancelled: "Cancelado",
};

// Retorna o status efetivo considerando captura ERP
const getEffectiveStatus = (order) => {
  if (order.status === "pending" && order.syncedAt) {
    return "received";
  }
  return order.status;
};

const PdvOrders = () => {
  const classes = useStyles();
  const { user, socket } = useContext(AuthContext);

  const [loading, setLoading] = useState(false);
  const [orders, dispatch] = useReducer(reducer, []);
  const [searchParam, setSearchParam] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [pageNumber, setPageNumber] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [lastRefresh, setLastRefresh] = useState(new Date());
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [refreshInterval, setRefreshInterval] = useState(() => {
    const saved = localStorage.getItem("pdvOrdersRefreshInterval");
    return saved ? parseInt(saved, 10) : 30000;
  });
  const refreshIntervalRef = useRef(null);

  const fetchOrders = useCallback(async (showLoadingIndicator = false) => {
    if (showLoadingIndicator) {
      setLoading(true);
    }
    setIsRefreshing(true);

    try {
      // Use PharmaFlow API
      const data = await pharmaApi.searchOrders({
        searchParam,
        status: statusFilter || undefined,
        pageNumber,
      });
      // API returns { data: [...], total, page, hasMore } format
      const orders = data.data || data.orders || (Array.isArray(data) ? data : []);
      dispatch({ type: "LOAD_ORDERS", payload: orders });
      setHasMore(data.hasMore || false);
      setLastRefresh(new Date());
    } catch (err) {
      toastError(err);
    } finally {
      setLoading(false);
      setIsRefreshing(false);
    }
  }, [searchParam, statusFilter, pageNumber]);

  // Initial fetch and when filters change (with debounce)
  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      fetchOrders(true);
    }, 500);

    return () => clearTimeout(delayDebounceFn);
  }, [fetchOrders]);

  // Auto-refresh interval
  useEffect(() => {
    if (refreshIntervalRef.current) {
      clearInterval(refreshIntervalRef.current);
    }

    if (refreshInterval > 0) {
      refreshIntervalRef.current = setInterval(() => {
        fetchOrders(false);
      }, refreshInterval);
    }

    return () => {
      if (refreshIntervalRef.current) {
        clearInterval(refreshIntervalRef.current);
      }
    };
  }, [fetchOrders, refreshInterval]);

  const handleRefreshIntervalChange = (e) => {
    const newInterval = e.target.value;
    setRefreshInterval(newInterval);
    localStorage.setItem("pdvOrdersRefreshInterval", newInterval);
  };

  // Socket listener for real-time updates
  useEffect(() => {
    if (!socket || !user?.companyId) return;

    const companyId = user.companyId;
    const onPdvOrder = (data) => {
      if (data.action === "update" || data.action === "create") {
        dispatch({ type: "UPDATE_ORDER", payload: data.order });
        setLastRefresh(new Date());
      }
    };

    socket.on(`company-${companyId}-pdvOrder`, onPdvOrder);
    return () => {
      socket.off(`company-${companyId}-pdvOrder`, onPdvOrder);
    };
  }, [socket, user]);

  const handleManualRefresh = () => {
    fetchOrders(false);
  };

  const handleViewOrder = async (orderId) => {
    try {
      // Use PharmaFlow API
      const data = await pharmaApi.getOrder(orderId);
      setSelectedOrder(data);
      setDetailsOpen(true);
    } catch (err) {
      toastError(err);
    }
  };

  const getStatusClass = (status) => {
    const classMap = {
      pending: classes.statusPending,
      received: classes.statusReceived,
      confirmed: classes.statusConfirmed,
      preparing: classes.statusPreparing,
      ready: classes.statusReady,
      dispatched: classes.statusDispatched,
      delivered: classes.statusDelivered,
      cancelled: classes.statusCancelled,
    };
    return classMap[status] || "";
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat("pt-BR", {
      style: "currency",
      currency: "BRL",
    }).format(value);
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleString("pt-BR");
  };

  return (
    <MainContainer>
      <MainHeader>
        <Title>OtimizAPP Pharma - Pedidos</Title>
        <MainHeaderButtonsWrapper>
          <div className={classes.headerActions}>
            <Tooltip title={`Última atualização: ${lastRefresh.toLocaleTimeString("pt-BR")}`}>
              <IconButton
                onClick={handleManualRefresh}
                disabled={isRefreshing}
                size="small"
              >
                <RefreshIcon className={isRefreshing ? classes.refreshing : ""} />
              </IconButton>
            </Tooltip>
          </div>
        </MainHeaderButtonsWrapper>
      </MainHeader>

      <Paper className={classes.mainPaper}>
        <Box className={classes.filterContainer}>
          <TextField
            placeholder="Buscar pedido..."
            variant="outlined"
            size="small"
            value={searchParam}
            onChange={(e) => setSearchParam(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
            style={{ minWidth: 250 }}
          />
          <FormControl variant="outlined" size="small" style={{ minWidth: 150 }}>
            <InputLabel>Status</InputLabel>
            <Select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              label="Status"
            >
              <MenuItem value="">Todos</MenuItem>
              <MenuItem value="pending">Pendente</MenuItem>
              <MenuItem value="confirmed">Confirmado</MenuItem>
              <MenuItem value="preparing">Em Separação</MenuItem>
              <MenuItem value="ready">Pronto</MenuItem>
              <MenuItem value="dispatched">Saiu para Entrega</MenuItem>
              <MenuItem value="delivered">Entregue</MenuItem>
              <MenuItem value="cancelled">Cancelado</MenuItem>
            </Select>
          </FormControl>
          <FormControl variant="outlined" size="small" style={{ minWidth: 150 }}>
            <InputLabel>Auto-refresh</InputLabel>
            <Select
              value={refreshInterval}
              onChange={handleRefreshIntervalChange}
              label="Auto-refresh"
            >
              {REFRESH_OPTIONS.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>
                  {opt.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>

        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Pedido</TableCell>
              <TableCell>Cliente</TableCell>
              <TableCell>Loja</TableCell>
              <TableCell>Total</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Data</TableCell>
              <TableCell align="center">Ações</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRowSkeleton columns={7} />
            ) : (
              orders.map((order) => (
                <TableRow key={order.id}>
                  <TableCell>{order.orderNumber}</TableCell>
                  <TableCell>{order.customer?.name || "-"}</TableCell>
                  <TableCell>{order.store?.name || "-"}</TableCell>
                  <TableCell>{formatCurrency(order.total)}</TableCell>
                  <TableCell>
                    <Chip
                      label={statusLabels[getEffectiveStatus(order)]}
                      size="small"
                      className={getStatusClass(getEffectiveStatus(order))}
                    />
                  </TableCell>
                  <TableCell>{formatDate(order.createdAt)}</TableCell>
                  <TableCell align="center">
                    <IconButton
                      size="small"
                      onClick={() => handleViewOrder(order.id)}
                    >
                      <VisibilityIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </Paper>

      {/* Order Details Dialog */}
      <Dialog
        open={detailsOpen}
        onClose={() => setDetailsOpen(false)}
        maxWidth="md"
        fullWidth
      >
        {selectedOrder && (
          <>
            <DialogTitle>
              Pedido #{selectedOrder.orderNumber}
              <Chip
                label={statusLabels[getEffectiveStatus(selectedOrder)]}
                size="small"
                className={getStatusClass(getEffectiveStatus(selectedOrder))}
                style={{ marginLeft: 16 }}
              />
            </DialogTitle>
            <DialogContent dividers className={classes.orderDetails}>
              {/* Cliente */}
              <Box mb={2} p={2} style={{ backgroundColor: "#f5f5f5", borderRadius: 8 }}>
                <Typography variant="subtitle1" gutterBottom>
                  <strong>Cliente</strong>
                </Typography>
                <Typography variant="body2">
                  <strong>Nome:</strong> {selectedOrder.customer?.name || "Não identificado"}
                </Typography>
                <Typography variant="body2">
                  <strong>WhatsApp:</strong> {selectedOrder.customer?.whatsapp || "-"}
                </Typography>
                {selectedOrder.customer?.cpf && (
                  <Typography variant="body2">
                    <strong>CPF:</strong> {selectedOrder.customer.cpf}
                  </Typography>
                )}
                {selectedOrder.customer?.email && (
                  <Typography variant="body2">
                    <strong>E-mail:</strong> {selectedOrder.customer.email}
                  </Typography>
                )}
              </Box>

              {/* Loja e Entrega */}
              <Box mb={2} p={2} style={{ backgroundColor: "#f5f5f5", borderRadius: 8 }}>
                <Typography variant="subtitle1" gutterBottom>
                  <strong>Entrega</strong>
                </Typography>
                <Typography variant="body2">
                  <strong>Tipo:</strong>{" "}
                  {selectedOrder.deliveryType === "pickup"
                    ? "Retirada na Loja"
                    : selectedOrder.deliveryType === "internal"
                    ? "Entrega Própria"
                    : "Entrega Externa"}
                </Typography>
                {selectedOrder.store && (
                  <Typography variant="body2">
                    <strong>Loja:</strong> {selectedOrder.store.name} ({selectedOrder.store.code})
                  </Typography>
                )}
                {selectedOrder.deliveryAddressSnapshot && (
                  <>
                    <Typography variant="body2" style={{ marginTop: 8 }}>
                      <strong>Endereço de Entrega:</strong>
                    </Typography>
                    <Typography variant="body2">
                      {selectedOrder.deliveryAddressSnapshot.street}, {selectedOrder.deliveryAddressSnapshot.number}
                      {selectedOrder.deliveryAddressSnapshot.complement && ` - ${selectedOrder.deliveryAddressSnapshot.complement}`}
                    </Typography>
                    <Typography variant="body2">
                      {selectedOrder.deliveryAddressSnapshot.neighborhood} - {selectedOrder.deliveryAddressSnapshot.city}/{selectedOrder.deliveryAddressSnapshot.state}
                    </Typography>
                    <Typography variant="body2">
                      CEP: {selectedOrder.deliveryAddressSnapshot.cep || selectedOrder.deliveryAddressSnapshot.zipCode}
                    </Typography>
                  </>
                )}
              </Box>

              <Divider style={{ margin: "16px 0" }} />

              {/* Itens do Pedido */}
              <Typography variant="subtitle1" gutterBottom>
                <strong>Itens do Pedido</strong>
              </Typography>
              <Table size="small" className={classes.itemsTable}>
                <TableHead>
                  <TableRow>
                    <TableCell>EAN</TableCell>
                    <TableCell>Produto</TableCell>
                    <TableCell align="right">Qtd</TableCell>
                    <TableCell align="right">Preço Unit.</TableCell>
                    <TableCell align="right">Desconto</TableCell>
                    <TableCell align="right">Total</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {selectedOrder.items?.map((item) => (
                    <TableRow key={item.id}>
                      <TableCell style={{ fontFamily: "monospace", fontSize: 12 }}>
                        {item.productBarcode || "-"}
                      </TableCell>
                      <TableCell>{item.productName || "Produto"}</TableCell>
                      <TableCell align="right">{item.quantity}</TableCell>
                      <TableCell align="right">
                        {formatCurrency(item.unitPrice)}
                      </TableCell>
                      <TableCell align="right" style={{ color: item.discountValue > 0 ? "green" : "inherit" }}>
                        {item.discountValue > 0 ? `-${formatCurrency(item.discountValue)}` : "-"}
                      </TableCell>
                      <TableCell align="right">
                        {formatCurrency(item.total)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>

              {/* Resumo Financeiro */}
              <Box mt={2} p={2} style={{ backgroundColor: "#e3f2fd", borderRadius: 8 }}>
                <Typography variant="body2">
                  <strong>Subtotal:</strong> {formatCurrency(selectedOrder.subtotal)}
                </Typography>
                {selectedOrder.discountTotal > 0 && (
                  <Typography variant="body2" style={{ color: "green" }}>
                    <strong>Desconto nos Itens:</strong> -{formatCurrency(selectedOrder.discountTotal)}
                  </Typography>
                )}
                {selectedOrder.deliveryFee > 0 && (
                  <Typography variant="body2">
                    <strong>Taxa de Entrega:</strong> {formatCurrency(selectedOrder.deliveryFee)}
                  </Typography>
                )}
                <Divider style={{ margin: "8px 0" }} />
                <Typography variant="h6">
                  <strong>Total:</strong> {formatCurrency(selectedOrder.total)}
                </Typography>
              </Box>

              {/* Observações */}
              {(selectedOrder.customerNotes || selectedOrder.internalNotes) && (
                <Box mt={2} p={2} style={{ backgroundColor: "#fff3e0", borderRadius: 8 }}>
                  <Typography variant="subtitle1" gutterBottom>
                    <strong>Observações</strong>
                  </Typography>
                  {selectedOrder.customerNotes && (
                    <>
                      <Typography variant="body2" color="textSecondary">
                        <strong>Cliente:</strong>
                      </Typography>
                      <Typography variant="body2" style={{ marginBottom: 8 }}>
                        {selectedOrder.customerNotes}
                      </Typography>
                    </>
                  )}
                  {selectedOrder.internalNotes && (
                    <>
                      <Typography variant="body2" color="textSecondary">
                        <strong>Interna:</strong>
                      </Typography>
                      <Typography variant="body2">{selectedOrder.internalNotes}</Typography>
                    </>
                  )}
                </Box>
              )}

              {/* Info ERP */}
              {selectedOrder.syncedAt && (
                <Box mt={2} p={2} style={{ backgroundColor: "#e8f5e9", borderRadius: 8 }}>
                  <Typography variant="subtitle1" gutterBottom>
                    <strong>Integração ERP</strong>
                  </Typography>
                  <Typography variant="body2">
                    <strong>Capturado em:</strong> {formatDate(selectedOrder.syncedAt)}
                  </Typography>
                  {selectedOrder.metadata?.erpOrderNumber && (
                    <Typography variant="body2">
                      <strong>Pedido ERP:</strong> {selectedOrder.metadata.erpOrderNumber}
                    </Typography>
                  )}
                  {selectedOrder.externalId && (
                    <Typography variant="body2">
                      <strong>ID Externo:</strong> {selectedOrder.externalId}
                    </Typography>
                  )}
                </Box>
              )}

              {/* Cancelamento */}
              {selectedOrder.status === "cancelled" && selectedOrder.cancellationReason && (
                <Box mt={2} p={2} style={{ backgroundColor: "#ffebee", borderRadius: 8 }}>
                  <Typography variant="subtitle1" gutterBottom style={{ color: "#c62828" }}>
                    <strong>Cancelamento</strong>
                  </Typography>
                  <Typography variant="body2">
                    <strong>Motivo:</strong> {selectedOrder.cancellationReason}
                  </Typography>
                  {selectedOrder.cancelledAt && (
                    <Typography variant="body2">
                      <strong>Data:</strong> {formatDate(selectedOrder.cancelledAt)}
                    </Typography>
                  )}
                </Box>
              )}

              {/* Datas do Pedido */}
              <Box mt={2}>
                <Typography variant="body2" color="textSecondary">
                  <strong>Criado em:</strong> {formatDate(selectedOrder.createdAt)}
                  {selectedOrder.confirmedAt && ` | Confirmado: ${formatDate(selectedOrder.confirmedAt)}`}
                  {selectedOrder.deliveredAt && ` | Entregue: ${formatDate(selectedOrder.deliveredAt)}`}
                </Typography>
              </Box>
            </DialogContent>
            <DialogActions>
              <Typography variant="caption" color="textSecondary" style={{ marginRight: 'auto', paddingLeft: 8 }}>
                O status do pedido é atualizado apenas pelo ERP
              </Typography>
              <Button onClick={() => setDetailsOpen(false)} color="primary" variant="contained">
                Fechar
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </MainContainer>
  );
};

export default PdvOrders;
